'use client'

import { ChevronRight, House, Mail, ShoppingCart, UserRound } from 'lucide-react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { MobileNav } from '@/components/molecules/mobile-nav.client'
import { isActive, navItems } from '@/lib/nav'
import { useCartCount } from '@/lib/use-cart-count'
import { useSessionUser } from '@/lib/use-session-user'
import { cn } from '@/lib/utils'

/**
 * The account menu, fetched only once there is an account.
 *
 * It is the last thing in the chrome that reaches better-auth's client — for
 * `signOut`, which cannot be anything else — and better-auth depends on `zod`.
 * Imported statically, that put 301 kB raw of a validation library into the
 * first load of every page on the site, `/pl/regulamin` included. Behind
 * `dynamic`, the bytes arrive when somebody is signed in and has an avatar to
 * open, and not one page sooner.
 *
 * `ssr: false` because the server pass never knows who is signed in anyway —
 * the whole point of reading the session in the browser is that `/pl/*` stays
 * prerendered.
 */
const UserMenu = dynamic(
  () => import('@/components/molecules/user-menu.client').then((m) => m.UserMenu),
  { ssr: false },
)

/**
 * The site's nav bar.
 *
 * Rendered once by `SiteShell` from a layout — never from inside a page — which
 * is what keeps it on screen across navigations instead of being torn down by a
 * sibling `loading.tsx`.
 *
 * Drawn from the owner's comp — see the note on the markup below.
 */

/** The bits of the session the bar actually renders. */
export type HeaderUser = {
  name?: string | null
  email: string
  image?: string | null
  role?: string | null
}

export function TopHeader({
  locale,
  initialUser,
}: {
  locale: string
  /**
   * The session, when the caller already resolved it server-side (the panel
   * does — it is gated on a session anyway). Public pages omit it and let the
   * client fetch, which keeps them free of `cookies()` and therefore statically
   * renderable; the cost is that a signed-in visitor sees the anonymous pill for
   * the one frame before the session lands. Inside the panel that frame would be
   * a flash on every load, hence the prop.
   */
  initialUser?: HeaderUser
}) {
  const pathname = usePathname()
  const count = useCartCount()
  const { user: session } = useSessionUser(initialUser)
  const [open, setOpen] = useState(false)
  const items = navItems(locale)
  // The bar is three groups cut from one list, so the mobile panel and the
  // bar can never drift: chips, the one entry drawn as a glyph, and the entry
  // that lives only in the panel.
  const barItems = items.filter((item) => item.slot === undefined)
  const contact = items.find((item) => item.slot === 'icon')
  const home = `/${locale}`
  const user: HeaderUser | null = session ?? null
  // Signed in, the account control belongs to the panel — sending someone who
  // already has a session to `/login` is the one destination they cannot use.
  const accountHref = user ? `/${locale}/panel` : `/${locale}/login`
  // Zero until the count comes back: the server pass has no idea what is in the
  // basket, so painting a number during SSR flashes the wrong one first.
  const showCount = count > 0

  // Close the panel when navigation lands somewhere new — the route change is
  // the confirmation that the tap worked, so leaving it open reads as a no-op.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger, not a value read here
  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      {/* The comp's nav, one to one (`Nav.dc.html`, styles `fn-*` in
          `styles/home-redesign.css`): a home tile, the links in one glass
          capsule with the current page in white, mail and cart tiles, and the
          white MyFLOU button. No bar behind them — each object is its own
          glass. `fixed`, with the comp's 18px/28px inset; the home page
          reserves the same 88px at its top. */}
      <header className="fixed inset-x-0 top-0 z-40" style={{ padding: '18px 28px' }}>
        <div className="fn-bar relative z-50">
          <div className="fn-side">
            <Link
              href={home}
              aria-label="FLOU, strona główna"
              title="Strona główna"
              className="fn-nv-sq fn-nv-glass"
            >
              <House size={20} strokeWidth={2} aria-hidden="true" />
            </Link>
          </div>

          <nav aria-label="Nawigacja główna" className="fn-nv-glass fn-nv-mid fn-capsule">
            {barItems.map((item) => (
              // Keyed by LABEL: Operator and Fotograf share an href.
              <Link
                key={item.label}
                href={item.href}
                aria-current={isActive(pathname, item.href) ? 'page' : undefined}
                className="fn-nv-link"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="fn-side fn-side-end">
            {contact ? (
              <Link
                href={contact.href}
                aria-label="Formularz kontaktowy"
                title="Formularz kontaktowy"
                className="fn-nv-sq fn-nv-glass fn-hide-sm"
              >
                <Mail size={20} strokeWidth={2} aria-hidden="true" />
              </Link>
            ) : null}
            <Link
              // The basket lives on the order screen, so this lands on its summary.
              href={`/${locale}/kreator#podsumowanie`}
              aria-label={showCount ? `Koszyk, ${count} w środku` : 'Koszyk'}
              title="Koszyk"
              className="fn-nv-sq fn-nv-glass relative"
            >
              <ShoppingCart size={20} strokeWidth={2} aria-hidden="true" />
              {showCount ? (
                <span
                  aria-hidden="true"
                  className="absolute -top-1 -right-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold leading-none text-[#16101c]"
                >
                  {count}
                </span>
              ) : null}
            </Link>
            {user ? (
              <UserMenu locale={locale} user={user} />
            ) : (
              <Link
                href={accountHref}
                aria-label="MyFLOU — Twój panel klienta"
                className="fn-nv-cta"
              >
                <span aria-hidden="true" className="fn-avatar">
                  <UserRound size={17} strokeWidth={2.2} />
                </span>
                <span className="fn-cta-label">
                  <span style={{ fontWeight: 700 }}>MyFLOU</span>
                  <span className="fn-cta-sub">Twój panel</span>
                </span>
                <ChevronRight size={16} aria-hidden="true" className="fn-cta-chev" />
              </Link>
            )}
            {/* Below 1100px the capsule is gone (the comp's own breakpoint)
                and this opens the full menu. One shape that morphs: the bars
                travel to the X and back. */}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-label={open ? 'Zamknij menu' : 'Otwórz menu'}
              className="fn-nv-sq fn-nv-glass fn-menu z-50"
            >
              <span aria-hidden="true" className="relative block h-3 w-[18px]">
                <span
                  className={cn(
                    'absolute left-0 h-[1.5px] w-[18px] rounded-full bg-current transition-all duration-300',
                    open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0',
                  )}
                />
                <span
                  className={cn(
                    'absolute right-0 h-[1.5px] rounded-full bg-current transition-all duration-300',
                    open ? 'top-1/2 w-[18px] -translate-y-1/2 -rotate-45' : 'bottom-0 w-3',
                  )}
                />
              </span>
            </button>
          </div>
        </div>

        {/* A SIBLING of the bar inside this `<header>`, not a portal: nothing
            on `<header>` may carry `transform`, `filter` or `clip-path`, or
            this `fixed inset-0` panel would be contained by it. */}
        <MobileNav
          items={items}
          pathname={pathname}
          open={open}
          onClose={() => setOpen(false)}
          accountHref={accountHref}
          accountLabel={user ? 'MyFLOU' : 'Zaloguj się'}
        />
      </header>
    </>
  )
}
